import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment from "moment";

import authHeader from "../services/auth-header";
import { API_BASE_URL } from "../common/config";
import useAxiosFetch from "../common/useAxiosFetch";
import AnimatedLoader from "../../img/AnimatedLoader";
import { getScoreBorderClass, canArchiveRisk } from "../common/helpers";
import { useAppContext } from "../AppContext";
import { MODAL_TYPES } from "../modals/ModalManager";
import "./PrepareRiskEvaluationPage.css";

const PRIORITY_META = {
    high: { className: "rep-priority-high", labelKey: "rep_priority_high", icon: "icon-level-high" },
    medium: { className: "rep-priority-medium", labelKey: "rep_priority_medium", icon: "icon-level-medium" },
    low: { className: "rep-priority-low", labelKey: "rep_priority_low", icon: "icon-level-low" },
};

const MAX_RISKS_OPTIONS = [5, 10, 15, 20, 30];
const DEFAULT_MAX_RISKS = 10;

// Cache AI results per org in sessionStorage so navigating to a risk and back
// doesn't re-run the whole analysis. Cleared when the browser tab closes.
const cacheKey = (orgId) => `rep_result_${orgId}`;

const readCache = (orgId) => {
    if (!orgId) return null;
    try {
        const raw = sessionStorage.getItem(cacheKey(orgId));
        return raw ? JSON.parse(raw) : null;
    } catch (e) {
        return null;
    }
};

const writeCache = (orgId, payload) => {
    if (!orgId) return;
    try {
        sessionStorage.setItem(cacheKey(orgId), JSON.stringify(payload));
    } catch (e) { /* storage full or unavailable — non-fatal */ }
};

const PrepareRiskEvaluationPage = ({ refreshData }) => {
    const { t, i18n } = useTranslation();
    const { currentOrg, currentUser, openModal } = useAppContext();
    const { data: featureLimits, refetch: refetchLimits } = useAxiosFetch("/api/features/limits");
    // Full risk objects, needed to open the "update score" workflow straight from a card.
    const { data: allRisks, refetch: refetchRisks } = useAxiosFetch("/api/risks/byOrg/" + currentOrg.id);

    const [orgChanges, setOrgChanges] = useState("");
    const [incidents, setIncidents] = useState("");
    const [maxRisks, setMaxRisks] = useState(DEFAULT_MAX_RISKS);

    const [result, setResult] = useState(null);
    const [isLoading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [ownerFilter, setOwnerFilter] = useState("");

    const resultRef = useRef(null);
    const abortController = useRef(null);
    const loadedOrgId = useRef(null);
    const resultOrgId = useRef(null);

    useEffect(() => {
        if (result && resultRef.current) {
            resultRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, [result]);

    // Pull fresh risk data back in after a score update was saved through the modal.
    useEffect(() => {
        if (refreshData) refetchRisks();
    }, [refreshData]);

    const aiUsage = featureLimits ? {
        current: featureLimits.usage.aiCalls.current,
        limit: featureLimits.limits.aiCallsPerWeek,
        resetsAt: featureLimits.usage.aiCalls.resetsAt,
    } : null;

    const getUsagePercentage = () => {
        if (!featureLimits) return 0;
        const limit = featureLimits.limits.aiCallsPerWeek;
        const current = featureLimits.usage.aiCalls.current;
        if (limit === -1) return 0;
        return Math.min((current / limit) * 100, 100);
    };

    const getUsageColor = () => {
        const percentage = getUsagePercentage();
        if (percentage >= 95) return "#e74c3c";
        if (percentage >= 80) return "#f39c12";
        return "#27ae60";
    };

    const prepare = async () => {
        // Client-side gate: if the weekly AI limit is already reached, prompt to upgrade.
        if (aiUsage && aiUsage.limit !== -1 && aiUsage.current >= aiUsage.limit) {
            openModal(MODAL_TYPES.UPGRADE, {
                featureType: "AI",
                currentLimit: aiUsage.limit,
                currentCount: aiUsage.current,
                recommendedPlan: "premium",
            });
            return;
        }

        if (abortController.current) abortController.current.abort();
        abortController.current = new AbortController();

        // Pin the org this request belongs to: a slow response must never be applied
        // to (or cached under) an organization the user switched to in the meantime.
        const requestOrgId = currentOrg.id;
        const isStale = () => requestOrgId !== loadedOrgId.current;

        setLoading(true);
        setErrorMessage(null);
        setResult(null);

        try {
            const response = await fetch(`${API_BASE_URL}/api/risks/prepare-evaluation/${requestOrgId}`, {
                method: "POST",
                headers: { ...authHeader(), "Content-Type": "application/json" },
                body: JSON.stringify({
                    orgChanges,
                    incidents,
                    maxRisks,
                    language: i18n.language === "nl" ? "nl" : "en",
                }),
                signal: abortController.current.signal,
            });

            if (response.status === 403) {
                const errorData = await response.json();
                if (errorData.upgradeRequired) {
                    if (isStale()) return;
                    openModal(MODAL_TYPES.UPGRADE, {
                        featureType: "AI",
                        currentLimit: errorData.limit,
                        currentCount: errorData.current,
                        recommendedPlan: "premium",
                    });
                    setLoading(false);
                    return;
                }
            }

            if (!response.ok) {
                let msg = t("rep_error", "Something went wrong while preparing the evaluation.");
                try {
                    const errorData = await response.json();
                    if (errorData?.msg) msg = errorData.msg;
                } catch (e) { /* keep default */ }
                if (isStale()) return;
                setErrorMessage(msg);
                setLoading(false);
                return;
            }

            const data = await response.json();
            if (isStale()) return;

            resultOrgId.current = requestOrgId;
            setResult(data);
            setOwnerFilter("");
            setLoading(false);
            refetchLimits();
        } catch (error) {
            if (error.name === "AbortError") return;
            if (isStale()) return;
            console.error("Failed to prepare risk evaluation:", error);
            setErrorMessage(t("rep_error", "Something went wrong while preparing the evaluation."));
            setLoading(false);
        }
    };

    // On mount (and org switch) restore a cached result if we have one. The evaluation
    // itself is only started manually, so the CISO can fill in the context first.
    useEffect(() => {
        if (!currentOrg?.id) return;
        if (loadedOrgId.current === currentOrg.id) return;
        loadedOrgId.current = currentOrg.id;

        // Drop any request still in flight for the organization we just left.
        if (abortController.current) abortController.current.abort();

        const cached = readCache(currentOrg.id);
        if (cached?.result) {
            resultOrgId.current = currentOrg.id;
            setResult(cached.result);
            setOrgChanges(cached.orgChanges || "");
            setIncidents(cached.incidents || "");
            setMaxRisks(cached.maxRisks || DEFAULT_MAX_RISKS);
            setErrorMessage(null);
            setLoading(false);
            return;
        }

        resultOrgId.current = null;
        setResult(null);
        setOrgChanges("");
        setIncidents("");
        setMaxRisks(DEFAULT_MAX_RISKS);
        setErrorMessage(null);
        setLoading(false);
    }, [currentOrg]);

    // Persist the result so it survives navigating to a risk and back. Always keyed
    // to the org the result was actually produced for.
    useEffect(() => {
        if (result && resultOrgId.current) {
            writeCache(resultOrgId.current, { result, orgChanges, incidents, maxRisks });
        }
    }, [result]);

    const formatLastReviewed = (risk) => {
        if (risk.neverReviewed || !risk.lastReviewedAt) return t("rep_never_reviewed", "Never reviewed");
        return `${moment(risk.lastReviewedAt).format("DD-MM-YYYY")} (${risk.daysSinceReview} ${t("rep_days_ago", "days ago")})`;
    };

    // Once the live risk list is loaded, drop suggestions that have since been archived.
    const suggestedRisks = (result?.risks || []).filter(risk => !Array.isArray(allRisks) || allRisks.some(item => item.id === risk.id));
    const owners = [...new Set(suggestedRisks.map(risk => risk.ownerName).filter(Boolean))].sort();
    const visibleRisks = ownerFilter
        ? suggestedRisks.filter(risk => risk.ownerName === ownerFilter)
        : suggestedRisks;

    // Look up the live risk record so a score updated through the modal is reflected
    // straight away, without spending another AI call.
    const findLiveRisk = (id) => (allRisks || []).find(item => item.id === id);
    const liveScore = (risk) => {
        const live = findLiveRisk(risk.id);
        if (!live) return risk.actualScore;
        return (parseInt(live["actual-chance"], 10) || 0) * (parseInt(live["actual-impact"], 10) || 0);
    };

    // Hand off to the regular risk overview, filtered down to the suggested risks.
    const risksUrl = visibleRisks.length
        ? `/risks?riskIds=${visibleRisks.map(risk => risk.id).join(",")}`
        : "/risks";

    // Open the same "update score" workflow used elsewhere in the app, pre-seeded
    // with the AI's reasoning so it ends up in the risk's update history.
    const evaluateRisk = (risk) => {
        const riskObj = findLiveRisk(risk.id);
        if (!riskObj) return;

        const prefix = t("rep_update_reason_prefix", "Risk evaluation");
        openModal(MODAL_TYPES.UPDATE_RISK_SCORE, {
            riskObj,
            updateReason: risk.reason ? `${prefix}: ${risk.reason}\n` : "",
        });
    };

    return (
        <div id="content">
            <div className="rep-container">
                {/* Header */}
                <div className="rep-header">
                    <h1 className="rep-title">
                        <i className="icon-evaluation rep-title-icon" />
                        {t("rep_title", "Risk Evaluation")}
                    </h1>
                    <p className="rep-subtitle">
                        {t("rep_subtitle", "AI automatically selects which risks to re-evaluate based on changed measures, scores that have been unchanged for a long time, the threat landscape and risks not reviewed for a long time.")}
                    </p>

                    {aiUsage && aiUsage.limit !== -1 && (
                        <div className="rep-usage-badge">
                            <i className="icon-bolt" style={{ color: getUsageColor() }} />
                            <span>{aiUsage.current} / {aiUsage.limit} {t("copilot_calls_used", "calls used")}</span>
                            {aiUsage.resetsAt && (
                                <span className="rep-usage-reset">
                                    {t("copilot_resets", "Resets")} {moment(aiUsage.resetsAt).format("MMM D")}
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {/* Context that the system can't derive by itself */}
                <div className="content-bubble rep-context">
                    <div className="rep-context-header">
                        <i className="icon-sliders" /> {t("rep_context_heading", "Context for the evaluation")}
                    </div>

                    <div className="rep-context-body">
                        <p className="rep-context-intro">
                            {t("rep_context_intro", "Add anything the system can't know, then start the evaluation. Changes and incidents are checked against risks of all levels, including low and recently evaluated risks whose scores may need to increase.")}
                        </p>

                        <div className="rep-field">
                            <label className="rep-label">
                                <i className="icon-sitemap1" /> {t("rep_org_changes", "Changes in the organization")}
                            </label>
                            <textarea
                                className="rep-textarea"
                                value={orgChanges}
                                maxLength={2000}
                                placeholder={t("rep_org_changes_placeholder", "e.g. Acquired a subsidiary, moved payroll to a new SaaS provider...")}
                                onChange={(e) => setOrgChanges(e.target.value)}
                            />
                        </div>

                        <div className="rep-field">
                            <label className="rep-label">
                                <i className="icon-incident" /> {t("rep_incidents", "Security incidents in the past period (short list)")}
                            </label>
                            <textarea
                                className="rep-textarea"
                                value={incidents}
                                maxLength={2000}
                                placeholder={t("rep_incidents_placeholder", "e.g. Phishing incident in finance, laptop lost, short outage of the customer portal...")}
                                onChange={(e) => setIncidents(e.target.value)}
                            />
                        </div>

                        <div className="rep-actions">
                            <label className="rep-max-risks">
                                <span>{t("rep_max_risks", "Max. risks to evaluate")}</span>
                                <select
                                    className="blue-dropdown"
                                    value={maxRisks}
                                    onChange={(e) => setMaxRisks(parseInt(e.target.value, 10))}
                                >
                                    {MAX_RISKS_OPTIONS.map(option => (
                                        <option key={option} value={option}>{option}</option>
                                    ))}
                                </select>
                            </label>

                            <button className="rep-prepare-btn" onClick={() => prepare()} disabled={isLoading}>
                                <i className="icon-stars1" />
                                {isLoading
                                    ? t("rep_preparing", "Preparing...")
                                    : result
                                        ? t("rep_regenerate_btn", "Regenerate selection")
                                        : t("rep_start_btn", "Start evaluation")}
                            </button>
                        </div>
                    </div>
                </div>

                {errorMessage && (
                    <div className="content-bubble rep-error">
                        <i className="icon-Risico" /> {errorMessage}
                    </div>
                )}

                {isLoading && (
                    <div className="content-bubble rep-loading">
                        <AnimatedLoader />
                        <p>{t("rep_analyzing", "Analyzing your risks and the described changes...")}</p>
                    </div>
                )}

                {/* Results */}
                {result && !isLoading && (
                    <div ref={resultRef}>
                        {/* Summary */}
                        <div className="content-bubble rep-summary">
                            <div className="rep-summary-item">
                                <span className="rep-summary-value">{suggestedRisks.length}</span>
                                <span className="rep-summary-label">{t("rep_selected", "Suggested for evaluation")}</span>
                            </div>
                            <div className="rep-summary-item">
                                <span className="rep-summary-value">{result.summary.totalRisks}</span>
                                <span className="rep-summary-label">{t("total_risks", "Total risks")}</span>
                            </div>
                            <div className="rep-summary-item">
                                <span className="rep-summary-value">{result.summary.measuresChangedNoScoreCount ?? 0}</span>
                                <span className="rep-summary-label">{t("rep_measures_no_score", "Measures changed, score unchanged")}</span>
                            </div>
                            <div className="rep-summary-item">
                                <span className="rep-summary-value">{result.summary.longstandingHighCount ?? 0}</span>
                                <span className="rep-summary-label">{t("rep_longstanding_high", "High for a long time")}</span>
                            </div>
                            <div className="rep-summary-item">
                                <span className="rep-summary-value">{result.summary.staleCount}</span>
                                <span className="rep-summary-label">{t("rep_stale", "Not reviewed recently")}</span>
                            </div>
                            <div className="rep-summary-item">
                                <span className="rep-summary-value">{result.summary.neverReviewedCount}</span>
                                <span className="rep-summary-label">{t("rep_never_reviewed", "Never reviewed")}</span>
                            </div>
                        </div>

                        {suggestedRisks.length > 0 ? (
                            <>
                                <div className="rep-result-toolbar">
                                    <div className="rep-result-heading">
                                        <i className="icon-checklist" />
                                        {t("rep_suggested_heading", "Risks suggested for evaluation")}
                                    </div>

                                    {owners.length > 1 && (
                                        <select
                                            className="blue-dropdown rep-owner-filter"
                                            value={ownerFilter}
                                            onChange={(e) => setOwnerFilter(e.target.value)}
                                        >
                                            <option value="">{t("rep_all_owners", "All risk owners")}</option>
                                            {owners.map(owner => (
                                                <option key={owner} value={owner}>{owner}</option>
                                            ))}
                                        </select>
                                    )}

                                    <div className="rep-selected-count">
                                        {visibleRisks.length} {t("rep_of", "of")} {suggestedRisks.length}
                                    </div>
                                </div>

                                <div className="rep-cards">
                                    {visibleRisks.map((risk) => {
                                        const priority = PRIORITY_META[risk.priority] || PRIORITY_META.medium;
                                        const score = liveScore(risk);
                                        const canEvaluate = !!findLiveRisk(risk.id);
                                        return (
                                            <div key={risk.id} className={`content-bubble rep-card rep-card-${risk.priority || "medium"}`}>
                                                <div className="rep-card-top">
                                                    <span className={`rep-priority ${priority.className}`}>
                                                        <i className={priority.icon} /> {t(priority.labelKey, risk.priority)}
                                                    </span>
                                                    <div className="rep-card-scores">
                                                        <span className="rep-score-label">{t("current", "Current")}</span>
                                                        <span className={getScoreBorderClass(score, currentOrg.rangeBounds)}>
                                                            {score}
                                                        </span>
                                                        <span className="rep-score-arrow">→</span>
                                                        <span className="rep-score-label">{t("target", "Target")}</span>
                                                        <span className="rep-score-target">{risk.expectedScore}</span>
                                                    </div>
                                                </div>

                                                <Link to={`/viewrisk?riskId=${risk.id}`} className="rep-card-title">
                                                    {risk.description}
                                                </Link>

                                                {risk.reason && (
                                                    <div className="rep-advice">
                                                        <div className="rep-advice-head">
                                                            <i className="icon-stars1" />
                                                            {t("rep_advice_heading", "Why evaluate this risk")}
                                                        </div>
                                                        <p className="rep-advice-text">{risk.reason}</p>
                                                    </div>
                                                )}

                                                <div className="rep-card-meta">
                                                    {risk.ownerName && (
                                                        <span className="rep-meta-item">
                                                            <i className="icon-user" /> {risk.ownerName}
                                                        </span>
                                                    )}
                                                    <span className="rep-meta-item">
                                                        <i className="icon-clock" /> {formatLastReviewed(risk)}
                                                    </span>
                                                    {risk.openMeasureCount > 0 && (
                                                        <span className="rep-meta-item">
                                                            <i className="icon-Maatregelen" /> {risk.openMeasureCount} {t("rep_open_measures", "open measures")}
                                                        </span>
                                                    )}
                                                    {risk.measuresChangedNoScoreUpdate && (
                                                        <span className="rep-tag rep-tag-changed">{t("rep_measures_no_score", "Measures changed, score unchanged")}</span>
                                                    )}
                                                    {risk.longstandingHigh && (
                                                        <span className="rep-tag rep-tag-high">{t("rep_longstanding_high", "High for a long time")}</span>
                                                    )}
                                                    {risk.isStale && (
                                                        <span className="rep-tag rep-tag-stale">{t("rep_stale", "Not reviewed recently")}</span>
                                                    )}
                                                </div>

                                                <div className="rep-card-actions">
                                                    <button
                                                        className="rep-evaluate-btn"
                                                        onClick={() => evaluateRisk(risk)}
                                                        disabled={!canEvaluate}
                                                    >
                                                        <i className="icon-score" /> {t("rep_evaluate_risk", "Evaluate risk")}
                                                    </button>
                                                    {canArchiveRisk(currentUser, findLiveRisk(risk.id)) && (
                                                        <button
                                                            className="rep-archive-btn"
                                                            onClick={() => openModal(MODAL_TYPES.ARCHIVE_RISK, { riskObj: findLiveRisk(risk.id) })}
                                                        >
                                                            <i className="icon-archive" /> {t("archive_risk", "Archive risk")}
                                                        </button>
                                                    )}
                                                    <Link to={`/viewrisk?riskId=${risk.id}`} className="rep-card-view-link">
                                                        {t("rep_view_risk", "View risk")}
                                                    </Link>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                <div className="rep-footer-actions">
                                    <Link to={risksUrl} className="rep-review-link">
                                        <i className="icon-list" /> {t("rep_go_to_risks", "Open these risks in the risk overview")}
                                    </Link>
                                </div>
                            </>
                        ) : (
                            <div className="content-bubble rep-empty">
                                <i className="icon-check-circle rep-empty-icon" />
                                <p>{t("rep_no_suggestions", "No risks need re-evaluation based on the information provided.")}</p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PrepareRiskEvaluationPage;
